const MIN_SIZE_TO_OPTIMIZE = 2 * 1024 * 1024
const MAX_LONG_EDGE = 2560
const PHOTO_QUALITY = 0.9
const OPTIMIZABLE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"])

export async function optimizeTaskPhoto(file: File) {
  if (!OPTIMIZABLE_TYPES.has(file.type) || file.size < MIN_SIZE_TO_OPTIMIZE) return file

  const sourceUrl = URL.createObjectURL(file)
  try {
    const image = await loadImage(sourceUrl)
    const scale = Math.min(1, MAX_LONG_EDGE / Math.max(image.naturalWidth, image.naturalHeight))
    const width = Math.max(1, Math.round(image.naturalWidth * scale))
    const height = Math.max(1, Math.round(image.naturalHeight * scale))
    const canvas = document.createElement("canvas")
    canvas.width = width
    canvas.height = height

    const context = canvas.getContext("2d")
    if (!context) return file
    context.drawImage(image, 0, 0, width, height)

    const outputType = file.type === "image/jpeg" ? "image/jpeg" : "image/webp"
    const optimized = await canvasToBlob(canvas, outputType, PHOTO_QUALITY)
    if (!optimized || optimized.size >= file.size) return file

    return new File([optimized], optimizedFilename(file.name, optimized.type), {
      type: optimized.type,
      lastModified: file.lastModified
    })
  } catch {
    return file
  } finally {
    URL.revokeObjectURL(sourceUrl)
  }
}

function loadImage(source: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error("Image decode failed"))
    image.src = source
  })
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number) {
  return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality))
}

function optimizedFilename(name: string, type: string) {
  const base = name.replace(/\.[^.]+$/, "") || "photo"
  return `${base}${type === "image/webp" ? ".webp" : ".jpg"}`
}
